import { useEffect, useRef, useState } from 'react';
import { apiFetch, ApiError, getSessionId } from '../../services/api-client';

// Task 2 (avatar chip): PLAYER card upload UI. Mirrors the header chip's
// pixel-corners/bg-pixel-cyan initials look for the 46px preview so the two
// stay visually identical whether or not an avatar is set. Client-side
// type/size checks mirror LogoUpload.tsx's — the server (Task 1) re-checks
// both, this is just fast feedback.
const ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

// The PLAYER card at the top of the MORE tab: shows the chosen player name
// (arcade handle) with an in-place editor. Names are cosmetic — identity is
// the wallet address, so shop analytics never move on rename — but they're
// what shops see, which is exactly why players who'd rather not be known by
// their Google name can change it (Bit-Street backlog N6).
// Self-contained: fetches its own profile so the (already huge) Dashboard
// only mounts it. Also owns the birthday editor (041): month/day only, and
// the server enforces a 300-day change lock — the UI mirrors it by hiding
// the edit affordance instead of saving into a 422.
//
// Task 10: visual-only restyle to the coral card language (white card, the
// name in Space Grotesk, the address in DotGothic). Every state variable,
// fetch, save call and the 300-day lock check below are byte-identical to
// before this task — only the JSX/classNames changed.

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const SELECT_CLASS =
  'pixel-corners-sm bg-background border-2 border-hairline px-3 py-1.5 font-grotesk font-medium text-xs text-primary outline-none focus:border-coral';

interface PlayerNameProps {
  shortAddress: string | null;
  userAddress?: string | null;
  onAvatarChange?: (url: string | null) => void;
}

export default function PlayerName({ shortAddress, userAddress, onAvatarChange }: PlayerNameProps) {
  const [name, setName] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [birthday, setBirthday] = useState<{ month: number; day: number } | null>(null);
  const [birthdayLocked, setBirthdayLocked] = useState(false);
  const [editingBday, setEditingBday] = useState(false);
  const [bdayMonth, setBdayMonth] = useState(1);
  const [bdayDay, setBdayDay] = useState(1);
  const [bdayBusy, setBdayBusy] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let dead = false;
    apiFetch<{
      displayName: string | null;
      avatarUrl: string | null;
      birthdayMonth: number | null;
      birthdayDay: number | null;
      birthdayUpdatedAt: string | null;
    }>(
      '/api/account', { action: 'profile-get' },
    )
      .then((r) => {
        if (dead) return;
        setName(r.displayName);
        setAvatarUrl(r.avatarUrl ?? null);
        // Dashboard's header chip holds its own avatarUrl state, seeded
        // separately — this self-corrects it once MORE opens and this
        // component's own profile-get lands, in case the two drifted.
        onAvatarChange?.(r.avatarUrl ?? null);
        if (r.birthdayMonth && r.birthdayDay) {
          setBirthday({ month: r.birthdayMonth, day: r.birthdayDay });
          if (r.birthdayUpdatedAt) {
            setBirthdayLocked(Date.now() < new Date(r.birthdayUpdatedAt).getTime() + 300 * 86_400_000);
          }
        }
      })
      .catch(() => { /* header falls back to the address */ });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAvatarFile(file: File) {
    if (!userAddress || avatarBusy) return;
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setAvatarError('PNG, JPG, OR WEBP ONLY');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError('MAX FILE SIZE: 2MB');
      return;
    }
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const formPayload = new FormData();
      formPayload.append('logo', file);
      formPayload.append('owner', userAddress);
      formPayload.append('kind', 'avatar');

      // verifyAuth requires Bearer <sessionId> — this is a bare multipart
      // fetch (not apiFetch), so the header has to be added by hand. Do NOT
      // set Content-Type: the browser must supply the multipart boundary.
      const sessionId = getSessionId();
      const uploadRes = await fetch('/api/shops/upload-logo', {
        method: 'POST',
        headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : undefined,
        body: formPayload,
      });
      if (!uploadRes.ok) {
        const body = await uploadRes.json().catch(() => null);
        throw new Error(body?.error || `Upload failed (${uploadRes.status})`);
      }
      const uploadData = await uploadRes.json();

      await apiFetch('/api/account', { action: 'profile-upsert', avatarUrl: uploadData.pixelUrl });
      setAvatarUrl(uploadData.pixelUrl);
      onAvatarChange?.(uploadData.pixelUrl);
    } catch (e) {
      setAvatarError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setAvatarBusy(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  }

  async function handleRemoveAvatar() {
    if (avatarBusy) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      await apiFetch('/api/account', { action: 'profile-upsert', avatarUrl: null });
      setAvatarUrl(null);
      onAvatarChange?.(null);
    } catch (e) {
      setAvatarError(e instanceof ApiError ? e.message : 'Remove failed');
    } finally {
      setAvatarBusy(false);
    }
  }

  const initials = name
    ? name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '☕';

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/account', { action: 'profile-upsert', displayName: draft });
      setName(draft.trim().replace(/\s+/g, ' '));
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function saveBirthday() {
    if (bdayBusy) return;
    setBdayBusy(true);
    setError(null);
    try {
      await apiFetch('/api/account', { action: 'profile-upsert', birthdayMonth: bdayMonth, birthdayDay: bdayDay });
      setBirthday({ month: bdayMonth, day: bdayDay });
      setBirthdayLocked(true);
      setEditingBday(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBdayBusy(false);
    }
  }

  return (
    <div className="bg-white pixel-corners p-[18px] flex flex-col gap-3">
      <p className="font-heading text-[8px] text-body-grey tracking-[1px] uppercase">Player</p>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-none w-[46px] h-[46px] pixel-corners bg-pixel-cyan flex items-center justify-center font-grotesk font-bold text-[15px] text-teal-ink overflow-hidden">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Your avatar"
              className="w-[46px] h-[46px] object-cover"
              style={{ imageRendering: 'pixelated' }}
            />
          ) : (
            initials
          )}
        </div>

        {userAddress && (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleAvatarFile(file);
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarBusy}
              className="pixel-corners-sm bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] px-3 py-1.5 disabled:opacity-50"
            >
              {avatarBusy ? 'UPLOADING…' : 'Upload avatar'}
            </button>
            {avatarUrl && (
              <button
                type="button"
                onClick={handleRemoveAvatar}
                disabled={avatarBusy}
                className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      {avatarError && <p className="font-grotesk font-medium text-[12px] text-error">{avatarError}</p>}

      <div className="flex items-center gap-2 flex-wrap">
        {editing ? (
          <span className="flex items-center gap-2 flex-wrap">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
              maxLength={20}
              autoFocus
              className="w-36 pixel-corners-sm bg-background border-2 border-hairline px-[14px] py-1.5 font-grotesk font-medium text-sm text-primary outline-none focus:border-coral"
            />
            <button onClick={save} disabled={busy} className="font-grotesk font-bold text-[13px] text-success disabled:opacity-50">
              {busy ? '…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="font-grotesk font-bold text-[13px] text-body-grey">
              Cancel
            </button>
          </span>
        ) : (
          <>
            <span className="font-grotesk font-bold text-[17px] text-primary">{name || shortAddress}</span>
            <button
              onClick={() => { setDraft(name || ''); setError(null); setEditing(true); }}
              aria-label="Edit player name"
              className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral"
            >
              Edit
            </button>
          </>
        )}
      </div>

      {shortAddress && <p className="font-body text-xs text-body-grey">{shortAddress}</p>}

      <div className="flex items-center gap-2 flex-wrap">
        {editingBday ? (
          <span className="flex items-center gap-2 flex-wrap">
            <select
              value={bdayMonth}
              onChange={(e) => {
                const m = Number(e.target.value);
                setBdayMonth(m);
                if (bdayDay > DAYS_IN_MONTH[m - 1]) setBdayDay(DAYS_IN_MONTH[m - 1]);
              }}
              className={SELECT_CLASS}
            >
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select
              value={bdayDay}
              onChange={(e) => setBdayDay(Number(e.target.value))}
              className={SELECT_CLASS}
            >
              {Array.from({ length: DAYS_IN_MONTH[bdayMonth - 1] }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
            <button onClick={saveBirthday} disabled={bdayBusy} className="font-grotesk font-bold text-[13px] text-success disabled:opacity-50">
              {bdayBusy ? '…' : 'Save'}
            </button>
            <button onClick={() => setEditingBday(false)} className="font-grotesk font-bold text-[13px] text-body-grey">
              Cancel
            </button>
          </span>
        ) : birthday ? (
          <span className="flex items-center gap-2">
            <span className="font-grotesk font-medium text-[13px] text-body-grey">
              {MONTHS[birthday.month - 1]} {birthday.day}
            </span>
            {!birthdayLocked && (
              <button
                onClick={() => { setBdayMonth(birthday.month); setBdayDay(birthday.day); setEditingBday(true); }}
                aria-label="Edit birthday"
                className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral"
              >
                Edit
              </button>
            )}
          </span>
        ) : (
          <button
            onClick={() => setEditingBday(true)}
            className="font-grotesk font-medium text-[13px] text-body-grey hover:text-coral"
          >
            Set birthday — shops give birthday perks
          </button>
        )}
      </div>

      {error && <p className="font-grotesk font-medium text-[12px] text-error">{error}</p>}
    </div>
  );
}
